/**
 * Runtime Event Bus
 * Mission 008.5: persistence + routing + replay + retention + aggregation.
 */

import {
  EventBusStatistics,
  EventFilter,
  EventHandler,
  EventPriority,
  EventPublishingOptions,
  EventQuery,
  EventSubscription,
  EventSubscriptionOptions,
  PersistedRuntimeEvent,
  RuntimeEvent,
  RuntimeEventSensitivity
} from './contracts'
import { RuntimeEventStore } from './persistence/store'
import { RuntimeEventAggregator } from './aggregation/aggregator'

export class RuntimeEventBus {
  private static readonly subscriptions = new Map<string, EventSubscription>()
  private static readonly store = new RuntimeEventStore()
  private static readonly eventQueue: PersistedRuntimeEvent[] = []
  private static processing = false

  private static stats: EventBusStatistics = {
    totalPublished: 0,
    totalProcessed: 0,
    totalPersisted: 0,
    activeSubscriptions: 0,
    queueDepth: 0,
    errorCount: 0,
    replayCount: 0
  }

  static async publish(event: RuntimeEvent, options: EventPublishingOptions = {}): Promise<void> {
    this.validateEvent(event)

    const persisted = options.persist === false ? this.toTransientEvent(event) : this.store.persist(event)
    this.stats.totalPublished += 1
    if (options.persist === false) {
      this.eventQueue.push(persisted)
    } else {
      this.stats.totalPersisted += 1
      this.eventQueue.push(persisted)
      RuntimeEventAggregator.consume(persisted)
    }

    this.stats.queueDepth = this.eventQueue.length

    if (!this.processing) {
      await this.processQueue()
    }

    if (options.synchronous) {
      const timeoutMs = options.timeoutMs || 3000
      const start = Date.now()
      while (this.eventQueue.some((queued) => queued.id === persisted.id)) {
        if (Date.now() - start > timeoutMs) break
        await new Promise((resolve) => setTimeout(resolve, 25))
      }
    }
  }

  static async publishBatch(events: RuntimeEvent[], options: EventPublishingOptions = {}): Promise<void> {
    for (const event of events) {
      await this.publish(event, options)
    }
  }

  static subscribe(handler: EventHandler, filter: EventFilter = {}, options: EventSubscriptionOptions = {}): EventSubscription {
    const subscription: EventSubscription = {
      id: `sub_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      filter,
      handler,
      options
    }

    this.subscriptions.set(subscription.id, subscription)
    this.stats.activeSubscriptions = this.subscriptions.size

    if (options.replayOnSubscribe) {
      const replayEvents = this.store.query({
        filter,
        limit: options.maxReplayEvents || 100,
        sortBy: 'sequence',
        sortOrder: 'desc'
      })
      this.stats.replayCount += replayEvents.length
      replayEvents.reverse().forEach((event) => {
        void Promise.resolve(handler(event))
      })
    }

    return subscription
  }

  static unsubscribe(subscription: EventSubscription): void {
    this.subscriptions.delete(subscription.id)
    this.stats.activeSubscriptions = this.subscriptions.size
  }

  static async queryEvents(query: EventQuery): Promise<PersistedRuntimeEvent[]> {
    return this.store.query(query)
  }

  static async replayEvents(filter: EventFilter = {}): Promise<PersistedRuntimeEvent[]> {
    const replay = this.store.replay(filter)
    this.stats.replayCount += replay.length
    return replay
  }

  static async replayExecution(executionId: string): Promise<PersistedRuntimeEvent[]> {
    const replay = this.store.replayByExecution(executionId)
    this.stats.replayCount += replay.length
    return replay
  }

  static async replayCorrelation(correlationId: string): Promise<PersistedRuntimeEvent[]> {
    const replay = this.store.replayByCorrelation(correlationId)
    this.stats.replayCount += replay.length
    return replay
  }

  static getStatistics(): EventBusStatistics {
    return { ...this.stats }
  }

  static getAggregationSnapshot() {
    return RuntimeEventAggregator.getSnapshot()
  }

  private static validateEvent(event: RuntimeEvent): void {
    if (!event.id || !event.type || !event.category || !event.source || !event.context || !event.timestamp || !event.data) {
      throw new Error('Invalid runtime event payload')
    }
  }

  private static toTransientEvent(event: RuntimeEvent): PersistedRuntimeEvent {
    return {
      ...event,
      sequence: -1,
      stream: event.metadata?.stream || 'runtime.events.transient',
      severity: event.metadata?.priority || EventPriority.NORMAL,
      sensitivity: event.metadata?.sensitivity || RuntimeEventSensitivity.PUBLIC,
      expiresAt: null,
      persistedAt: new Date()
    }
  }

  private static async processQueue(): Promise<void> {
    this.processing = true

    try {
      while (this.eventQueue.length > 0) {
        const event = this.eventQueue.shift()!
        this.stats.queueDepth = this.eventQueue.length
        await this.processEvent(event)
      }
    } finally {
      this.processing = false
    }
  }

  private static async processEvent(event: PersistedRuntimeEvent): Promise<void> {
    const subscriptions = Array.from(this.subscriptions.values()).filter((subscription) => this.matchesFilter(event, subscription.filter))

    for (const subscription of subscriptions) {
      try {
        await subscription.handler(event)
      } catch {
        this.stats.errorCount += 1
      }
    }

    this.stats.totalProcessed += 1
  }

  private static matchesFilter(event: PersistedRuntimeEvent, filter: EventFilter): boolean {
    if (filter.types && !filter.types.includes(event.type)) return false
    if (filter.categories && !filter.categories.includes(event.category)) return false
    if (filter.sources && !filter.sources.includes(event.source)) return false
    if (filter.priorities && !filter.priorities.includes(event.severity)) return false
    if (filter.streams && !filter.streams.includes(event.stream)) return false

    if (filter.executionId && event.context.executionId !== filter.executionId) return false
    if (filter.correlationId && event.context.correlationId !== filter.correlationId) return false
    if (filter.actor && event.context.actor !== filter.actor) return false
    if (filter.route && event.context.route !== filter.route) return false
    if (filter.service && event.context.service !== filter.service) return false
    if (filter.domain && event.context.domain !== filter.domain) return false

    if (filter.afterSequence && event.sequence <= filter.afterSequence) return false
    if (filter.from && event.timestamp < filter.from) return false
    if (filter.to && event.timestamp > filter.to) return false
    if (filter.customFilter && !filter.customFilter(event)) return false

    return true
  }
}
